import {CircleAlert, Inbox, RefreshCw} from 'lucide-react';
import {ReactNode} from 'react';
import {SecondaryButton, Surface} from '../design';

export const PageHeader = ({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) => (
  <header className="pd-page-header">
    <div>
      {eyebrow ? <span className="pd-eyebrow">{eyebrow}</span> : null}
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
    {actions ? <div className="pd-page-actions">{actions}</div> : null}
  </header>
);

export const LoadingPanel = ({label = 'Loading…'}: {label?: string}) => (
  <div className="pd-loading-panel" aria-busy="true" aria-live="polite">
    <span className="pd-loading-line" />
    <span className="pd-loading-line" />
    <span className="pd-loading-line" />
    <span className="pd-visually-hidden">{label}</span>
  </div>
);

export const ErrorPanel = ({error, retry}: {error: unknown; retry?: () => void}) => (
  <Surface className="pd-message-panel pd-message-panel--error" role="alert">
    <CircleAlert size={24} aria-hidden="true" />
    <div>
      <h3>That part of your desk did not load.</h3>
      <p>{error instanceof Error ? error.message : 'Try again in a moment.'}</p>
    </div>
    {retry ? (
      <SecondaryButton
        leftSection={<RefreshCw size={16} aria-hidden="true" />}
        onClick={retry}
      >
        Try again
      </SecondaryButton>
    ) : null}
  </Surface>
);

export const EmptyPanel = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) => (
  <Surface className="pd-message-panel pd-message-panel--empty">
    <Inbox size={28} aria-hidden="true" />
    <div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
    {action}
  </Surface>
);

export const formatRelativeDate = (value?: string): string => {
  if (!value) return 'No date';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'Unknown date';
  const difference = timestamp - Date.now();
  const formatter = new Intl.RelativeTimeFormat(undefined, {numeric: 'auto'});
  const day = Math.round(difference / 86_400_000);
  if (Math.abs(day) >= 1) return formatter.format(day, 'day');
  const hour = Math.round(difference / 3_600_000);
  if (Math.abs(hour) >= 1) return formatter.format(hour, 'hour');
  const minute = Math.round(difference / 60_000);
  return formatter.format(minute, 'minute');
};

export const formatDateTime = (value?: string): string => {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};
